/*
Author: Chathuraka mallawa Arachchi
Date: 03/02/2025
Copyright © 2024 Infix!. All rights reserved.
*/

import trash from '@/assets/trash.svg'
import withAlerts from '@/components/HOC/withAlerts'
import { useUrlType } from '@/components/hooks'
import {
    IMatrixItem,
    INode,
    Ioption,
    nodeConfigurations,
    NodeProps,
} from '@/components/survey/SurveyModel/types/nodeTypes'
import { constants } from '@/constants'
import { SingleSelect } from '@/constants/constants'
import { RootState } from '@/reducers/store'
import { useAddNewField } from '@/state/survey/formSlice'
import { removeField } from '@/state/survey/formSlice'
import {
    AnswerValueTypes,
    BasicAnswerSave,
} from '@/state/survey/saveAnswerSlice'
import { Utility } from '@/utils'
import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import BloodThinningMedicines from '../CustomizedSurveyNodes/BloodThinningMedicines/BloodThinningMedicines'
import CommentNode from '../CustomizedSurveyNodes/Comment/CommentNode'
import DatePicker from '../CustomizedSurveyNodes/DatePicker/DatePicker'
import DateRangeMatrixNode from '../CustomizedSurveyNodes/DateRangeMatrix/DateRangeMatrixNode'
import DropDownNode from '../CustomizedSurveyNodes/DropDown/DropDownNode'
import HealthImprovementPOA from '../CustomizedSurveyNodes/HealthImprovement/HealthImprovementPOA'
import HealthImprovmentPatient from '../CustomizedSurveyNodes/HealthImprovement/HealthImprovmentPatient'
import QuestionTextOnly from '../CustomizedSurveyNodes/QuestionTextOnly/QuestionTextOnly'
import SingleCheckBox from '../CustomizedSurveyNodes/SingleCheckBox/SingleCheckBox'
import SingleChoiceNode from '../CustomizedSurveyNodes/SingleChoice/SingleChoiceNode'
import SingleLineText from '../CustomizedSurveyNodes/SingleLineText/SingleLineText'
import TelephoneNo from '../CustomizedSurveyNodes/TelephoneNo/TelephoneNo'
import Textarea from '../CustomizedSurveyNodes/Textarea/Textarea'
import { ValidationManager } from '../SurveyModel'
import Debug from './Debug'
import FormRow from './FormRow'
import HeightInput from './HeightInput'
import MultipleChoiceInput from './MultipleChoiceInput'
import NumberInput from './NumberInput'
import QuestionSet from './QuestionSet'
import WeightInput from './WeightInput'

const nodeMap: { [key: string]: React.FC } = {
    SingleLineText: withAlerts(SingleLineText),
    TelephoneNo: withAlerts(TelephoneNo),
    number: withAlerts(NumberInput),
    weight: withAlerts(WeightInput),
    height: withAlerts(HeightInput),
    SingleChoice: withAlerts(SingleChoiceNode),
    multiChoice: withAlerts(MultipleChoiceInput),
    SingleSelect: withAlerts(DropDownNode),
    HealthImprovementPOA: withAlerts(HealthImprovementPOA),
    HealthImprovmentPatient: withAlerts(HealthImprovmentPatient),
    DateRangeMatrix: withAlerts(DateRangeMatrixNode),
    Comment: withAlerts(CommentNode),
    QuestionSet: withAlerts(QuestionSet),
    FormRow: withAlerts(FormRow),
    AdvancedMultiChoiceCheckbox: withAlerts(BloodThinningMedicines),
    QuestionTextOnly: withAlerts(QuestionTextOnly),
    Textarea: withAlerts(Textarea),
    DatePicker: withAlerts(DatePicker),
    SingleCheckBox: withAlerts(SingleCheckBox),
}

interface MatrixMultipleProps {
    node: INode
    onBlur: (rows: IMatrixItem[]) => void
    value?: any
    disabled?: boolean
    propClasses?: string
    onLoadComplete?: () => void
}
const MatrixMultiple: React.FC<MatrixMultipleProps> = (
    props: MatrixMultipleProps
) => {
    const [rows, setRows] = useState<IMatrixItem[]>([])

    const { node, disabled = false, propClasses, onBlur, onLoadComplete } = props
    const hasCalledOnLoadComplete = useRef(false)
    const {
        addLabel = 'Add another +',
        repeatedNodes,
        removeLabel = 'Remove the above medicine',
    } = node
    const form = useSelector((state: RootState) => state.form)
    const isInitialRun = useRef(true)
    const { isPatient } = useUrlType()
    const isInitialRunAnswers = useRef(true)
    const addNewFieldValue = useAddNewField()
    const dispatch = useDispatch()

    useEffect(() => {
        if (!rows.length && repeatedNodes && isInitialRun.current) {
            isInitialRun.current = false
            addNewRow()
        }
    }, [repeatedNodes, rows])

    const addUUIDToNodesWithValues = (
        nodes: INode[],
        parentUUID: string,
        child: boolean,
        parent: INode | null,
        answerRow: BasicAnswerSave[]
    ): INode[] => {
        return nodes.map((n: INode) => {
            const otherOption: nodeConfigurations | null | undefined =
                parent &&
                parent.configurations &&
                parent.configurations.find(
                    (Item) => Item.key === 'OtherOptionValue'
                )
            let savedValue: AnswerValueTypes = ''
            const selectedAnswer: BasicAnswerSave | undefined = answerRow.find(
                (answer: BasicAnswerSave) => answer.name === n.name
            )

            if (n.type === SingleSelect) {
                //answer 'Other' value is not available in the options dropdown. The value has to be set to "Other"
                const otherValue: nodeConfigurations | null | undefined =
                    n &&
                    n.configurations &&
                    n.configurations.find(
                        (Item) => Item.key === 'OtherOptionValue'
                    )
                if (
                    !child &&
                    selectedAnswer &&
                    n.options?.find(
                        (option): option is Ioption =>
                            typeof option === 'object' &&
                            'name' in option &&
                            option.name === otherValue?.value
                    ) &&
                    !n.options?.find(
                        (option): option is Ioption =>
                            typeof option === 'object' &&
                            'name' in option &&
                            option.name === selectedAnswer.value
                    )
                ) {
                    savedValue = otherValue?.value as string
                } else if (selectedAnswer) {
                    savedValue = selectedAnswer.value
                }
            } else if (
                child &&
                parent &&
                parent.type === SingleSelect &&
                parent.options?.find(
                    (option): option is Ioption =>
                        typeof option === 'object' &&
                        'name' in option &&
                        option.name === otherOption?.value
                )
            ) {
                //Child value is available in "Other" option of parent node
                const parentAnswer: BasicAnswerSave | undefined =
                    answerRow.find(
                        (answer: BasicAnswerSave) => answer.name === parent.name
                    )
                savedValue = parentAnswer ? parentAnswer.value : ''
            } else if (selectedAnswer) {
                savedValue = selectedAnswer.value
            }

            const newUUID = parentUUID
            let nodeName = `${n.name}-node-${newUUID}`
            if (child && parent && parent.type !== constants.FormRow) {
                nodeName = `${n.name}-${parent.name}-node-${newUUID}`
            }
            const newNode = {
                ...n,
                name: nodeName,
                nodes: n.nodes
                    ? addUUIDToNodesWithValues(
                          n.nodes,
                          newUUID,
                          true,
                          n,
                          answerRow
                      )
                    : [],
            }

            // Apply validation
            const validatedForm =
                ValidationManager.applyCommonValidationsWithValue(
                    newNode,
                    savedValue
                )
            addNewFieldValue(newNode.name, validatedForm)

            return newNode
        })
    }

    useEffect(() => {
        if (isInitialRunAnswers.current && node) {
            isInitialRunAnswers.current = false
            const localRows: IMatrixItem[] = []
            if (typeof form[node.name]?.value === 'string') {
                const nodeValue = form[node.name]?.value
                    ? JSON.parse(form[node.name].value as string) // Safely cast to string
                    : []
                if (Array.isArray(nodeValue) && nodeValue.length > 0) {
                    nodeValue.map((answerRow: BasicAnswerSave[]) => {
                        const parentUUID = uuidv4()
                        const newRow: IMatrixItem = {
                            index: parentUUID,
                            nodes: node?.repeatedNodes
                                ? addUUIDToNodesWithValues(
                                      node.repeatedNodes,
                                      parentUUID,
                                      false,
                                      null,
                                      answerRow
                                  )
                                : [],
                        }
                        localRows.push(newRow)
                    })
                    setRows(localRows)
                } else {
                    addNewRow()
                }
            }
        }
    }, [node, isInitialRunAnswers, addNewFieldValue, form])

    useEffect(() => {
        if (rows.length > 0 && !hasCalledOnLoadComplete.current) {
            hasCalledOnLoadComplete.current = true
            setTimeout(() => {
                onLoadComplete?.()
            }, 0);
        }
    }, [rows])

    const onBlurEvent = () => {
        onBlur(rows)
    }

    const returnComponent = (
        nodeObj: INode,
        nodeIndex: number,
        classProrp: string,
        haschildren?: boolean,
        component?: ReactNode
    ) => {
        const Component =
            (nodeMap[nodeObj.type] as unknown as React.FC<NodeProps>) || Debug
        const name = nodeObj.name
        const width = nodeObj.type === constants.Textarea ? '' : 'w-1/2'
        const widthClass = Utility.getClassForWidth(nodeObj)
        return (
            <React.Fragment key={`${name}-${nodeIndex}`}>
                {/* {name} {'--- node name'} */}
                <div
                    className={`${node?.repeatedNodes?.length === 1 || widthClass === 'width-half' ? width : `flex-1 ${classProrp}`} gap-2`}
                >
                    <div id={nodeObj.name} className="absolute"></div>
                    <Component
                        repeatedElement={true}
                        onBlur={onBlurEvent}
                        disabled={
                            Object.prototype.hasOwnProperty.call(
                                node,
                                'disabled'
                            ) && node.disabled
                                ? node.disabled
                                : disabled
                        }
                        key={name}
                        className={node.type}
                        node={nodeObj}
                        name={name}
                    >
                        {haschildren && nodeObj.nodes
                            ? nodeObj.nodes.map((n: INode, i: number) =>
                                  returnComponent(n, i, 'w-full')
                              )
                            : null}
                        {nodeObj.type === constants.FormRow && nodeIndex === 0
                            ? component
                            : ''}
                    </Component>
                </div>
            </React.Fragment>
        )
    }

    const renderInnerNode = (node: INode, nodeIndex: number) => {
        return (
            node &&
            node?.nodes?.map((innerNode: INode, childIndex: number) => {
                const exp = innerNode.showIf
                function getRemainingString(name: string): string {
                    return name.split('-').slice(1).join('-')
                }
                if (exp) {
                    const updatedStr = exp.replace(
                        /{[^}]+}/g,
                        `{${getRemainingString(innerNode.name)}}`
                    )
                    if (
                        innerNode.showIf &&
                        Utility.evaluateManually(form, updatedStr)
                    ) {
                        return returnComponent(
                            innerNode,
                            nodeIndex + childIndex,
                            'w-full'
                        )
                    }
                }

                return null
            })
        )
    }

    const renderComponents = (rowElement: IMatrixItem, index: number) => {
        return (
            <React.Fragment key={`${index}-row`}>
                <div
                    className={`Question-raw-wrapper ${rowElement.nodes.some((node) => node.type === constants.FormRow) ? '' : 'flex'} ${isPatient ? 'flex-col w-full' : ''} gap-2`}
                >
                    {rowElement &&
                        rowElement.nodes.map(
                            (node: INode, nodeIndex: number) => {
                                return (
                                    <React.Fragment>
                                        {returnComponent(
                                            node,
                                            nodeIndex,
                                            '',
                                            node.type === constants.FormRow
                                                ? true
                                                : false,
                                            <React.Fragment>
                                                {rows.length - 1 === 0 ? (
                                                    <></>
                                                ) : (
                                                    <div
                                                        onClick={() =>
                                                            removeAnswer(
                                                                rowElement.index
                                                            )
                                                        }
                                                        className="items-center trash-icon"
                                                    >
                                                        <img
                                                            style={{
                                                                display:
                                                                    disabled
                                                                        ? 'none'
                                                                        : 'block',
                                                            }}
                                                            src={trash}
                                                        />
                                                    </div>
                                                )}
                                            </React.Fragment>
                                        )}
                                        {isPatient
                                            ? renderInnerNode(node, nodeIndex)
                                            : null}
                                    </React.Fragment>
                                )
                            }
                        )}
                    {!isPatient &&
                    !rowElement.nodes.some(
                        (node) => node.type === constants.FormRow
                    ) &&
                    rows.length - 1 != 0 ? (
                        <div
                            onClick={() => removeAnswer(rowElement.index)}
                            className="items-center trash-icon"
                        >
                            <img
                                style={{ display: disabled ? 'none' : 'block' }}
                                src={trash}
                            />
                        </div>
                    ) : (
                        ''
                    )}
                </div>
                {!isPatient ? (
                    <div className="Question-raw-wrapper matrix-sub-node flex flex-col w-full gap-2">
                        {rowElement &&
                            rowElement.nodes.map(
                                (node: INode, nodeIndex: number) => {
                                    return renderInnerNode(node, nodeIndex)
                                }
                            )}
                    </div>
                ) : (
                    ''
                )}

                {isPatient && rows.length > 1 ? (
                    <div className="remove-link flex items-center justify-center">
                        <button
                            type="button"
                            onClick={() => removeAnswer(rowElement.index)}
                        >{`${removeLabel}`}</button>
                    </div>
                ) : (
                    ''
                )}
            </React.Fragment>
        )
    }

    //Check user has entered data for all the fields:
    //Assumption - all the fields are required to add another row
    //ToDo: check only given required fields
    const checkLastRowData = () => {
        let completed: boolean = true
        rows.forEach((rowElement: IMatrixItem) => {
            rowElement.nodes.map((node: INode) => {
                if (
                    node.type != constants.SingleCheckBox &&
                    node.type !== constants.FormRow &&
                    !form[node.name].value &&
                    node.validations &&
                    node.validations.length &&
                    node.validations.some(
                        (validation) => validation.type === 'required'
                    )
                )
                    completed = false

                if (node && node?.nodes) {
                    node?.nodes?.map((innerNode: INode) => {
                        const exp = innerNode.showIf
                        function getRemainingString(name: string): string {
                            return name.split('-').slice(1).join('-')
                        }
                        if (exp) {
                            const updatedStr = exp.replace(
                                /{[^}]+}/g,
                                `{${getRemainingString(innerNode.name)}}`
                            )
                            if (
                                innerNode.showIf &&
                                Utility.evaluateManually(form, updatedStr)
                            ) {
                                if (!form[innerNode.name].value)
                                    completed = false
                            }
                        } else {
                            if (
                                innerNode.type != constants.SingleCheckBox &&
                                innerNode.type !== constants.FormRow &&
                                !form[innerNode.name].value &&
                                innerNode.validations &&
                                innerNode.validations.length &&
                                innerNode.validations.some(
                                    (validation) =>
                                        validation.type === 'required'
                                )
                            )
                                completed = false
                        }

                        return null
                    })
                }
            })
        })

        return completed
    }

    const addUUIDToNodes = (
        nodes: INode[],
        parentUUID: string,
        child: boolean,
        parent: INode | null
    ): INode[] => {
        return nodes.map((n) => {
            const newUUID = parentUUID
            let nodeName = `${n.name}-node-${newUUID}`
            if (child && parent && parent.type !== constants.FormRow) {
                nodeName = `${n.name}-${parent.name}-node-${newUUID}`
            }
            const newNode = {
                ...n,
                name: nodeName,
                nodes: n.nodes ? addUUIDToNodes(n.nodes, newUUID, true, n) : [],
            }

            // Apply validation
            const validatedForm =
                ValidationManager.applyCommonValidationsWithValue(newNode, '')
            addNewFieldValue(newNode.name, validatedForm)

            return newNode
        })
    }

    const addNewRow = () => {
        const parentUUID = uuidv4()
        const newRow: IMatrixItem = {
            index: parentUUID,
            nodes: node?.repeatedNodes
                ? addUUIDToNodes(node.repeatedNodes, parentUUID, false, null)
                : [],
        }
        setRows([...rows, newRow])
    }

    const removeAnswer = (index: any) => {
        const newRows = rows.filter((item) => item.index !== index)
        const removedRow = rows.find((item) => item.index === index)
        if (removedRow) {
            removedRow.nodes.forEach((node) => {
                dispatch(removeField({ field: node.name }))
                if (node.nodes) {
                    node.nodes.forEach((innerNode) => {
                        dispatch(removeField({ field: innerNode.name }))
                    })
                }
            })
        }
        setRows(newRows)
        onBlur(newRows)
    }

    return (
        <div
            tabIndex={0}
            // onBlur={onBlurEvent}
            className={`matrix ${propClasses ? propClasses : propClasses}`}
        >
            {rows.map((rowElement: IMatrixItem, index) => {
                return (
                    <div
                        className={`${rows.length - 1 === index ? 'last ' : ''} ${index === 0 ? 'first' : ''} repeated-node`}
                        key={index}
                    >
                        {renderComponents(rowElement, index)}
                    </div>
                )
            })}
            {isPatient ? (
                <div
                    className={`add-link flex items-center justify-center ${checkLastRowData() ? 'enable' : ''} `}
                >
                    <button
                        className=""
                        type="button"
                        onClick={() => checkLastRowData() && addNewRow()}
                    >
                        {addLabel}
                    </button>
                </div>
            ) : (
                <div
                    style={{ display: disabled ? 'none' : 'block' }}
                    className="add-link-poa flex"
                >
                    <button
                        className=""
                        type="button"
                        onClick={() => checkLastRowData() && addNewRow()}
                    >
                        {addLabel}
                    </button>
                </div>
            )}
        </div>
    )
}

export default MatrixMultiple
